import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import spottoLogo from '../assets/spotto-logo.png';

export const AccountDeletion: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [email, setEmail] = useState(user?.email || '');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!confirmed) {
      setError('Musisz potwierdzić, że rozumiesz konsekwencje usunięcia konta.');
      return;
    }

    if (!email) {
      setError('Adres e-mail jest wymagany.');
      return;
    }

    try {
      setIsSubmitting(true);
      await authService.requestAccountDeletion({ email, reason });
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Wystąpił błąd podczas wysyłania żądania. Spróbuj ponownie.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-surface-app">
        {/* Header */}
        <header className="bg-surface-card border-b border-border-subtle">
          <div className="max-w-4xl mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <img
                  src={spottoLogo}
                  alt="Spotto Logo"
                  className="h-10 w-auto"
                />
                <h1 className="text-xl font-bold text-white">Spotto</h1>
              </div>
              <Link
                to={isAuthenticated ? "/" : "/login"}
                className="text-brand-primary hover:text-brand-primary transition-colors text-sm font-medium"
              >
                {isAuthenticated ? 'Powrót do aplikacji' : 'Powrót do logowania'}
              </Link>
            </div>
          </div>
        </header>

        {/* Success Message */}
        <main className="max-w-4xl mx-auto px-6 py-12">
          <div className="bg-surface-card rounded-2xl border border-border-subtle p-8 lg:p-12 shadow-xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-brand-confirm/100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-white mb-4">
                Żądanie zostało wysłane
              </h1>
              <p className="text-ink-primary mb-6">
                Twoje żądanie usunięcia konta zostało przekazane do naszego zespołu.
                Skontaktujemy się z Tobą w ciągu 30 dni w celu potwierdzenia i realizacji żądania.
              </p>
              <p className="text-ink-secondary text-sm">
                Sprawdź swoją skrzynkę e-mail: <strong className="text-white">{email}</strong>
              </p>
              <div className="mt-8">
                <Link
                  to={isAuthenticated ? "/" : "/login"}
                  className="inline-block px-6 py-3 bg-brand-primary/50 hover:bg-brand-primary text-white font-medium rounded-lg transition-colors"
                >
                  {isAuthenticated ? 'Powrót do aplikacji' : 'Powrót do logowania'}
                </Link>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-app">
      {/* Header */}
      <header className="bg-surface-card border-b border-border-subtle">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img
                src={spottoLogo}
                alt="Spotto Logo"
                className="h-10 w-auto"
              />
              <h1 className="text-xl font-bold text-white">Spotto</h1>
            </div>
            <Link
              to={isAuthenticated ? "/" : "/login"}
              className="text-brand-primary hover:text-brand-primary transition-colors text-sm font-medium"
            >
              {isAuthenticated ? 'Powrót do aplikacji' : 'Powrót do logowania'}
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-surface-card rounded-2xl border border-border-subtle p-8 lg:p-12 shadow-xl">
          {/* Title */}
          <div className="text-center mb-12">
            <h1 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Usunięcie konta i danych
            </h1>
            <div className="w-24 h-1 bg-brand-primary/50 mx-auto"></div>
          </div>

          {/* Warning Section */}
          <div className="bg-brand-warm/10 border border-brand-warm/40 rounded-lg p-6 mb-8">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-brand-warm-deep mr-4 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h3 className="text-lg font-bold text-brand-warm-deep mb-2">Ważne informacje</h3>
                <ul className="space-y-2 text-ink-primary text-sm">
                  <li className="flex items-start">
                    <span className="text-brand-warm-deep mr-2">•</span>
                    Usunięcie konta jest operacją nieodwracalną
                  </li>
                  <li className="flex items-start">
                    <span className="text-brand-warm-deep mr-2">•</span>
                    Wszystkie Twoje dane osobowe zostaną trwale usunięte
                  </li>
                  <li className="flex items-start">
                    <span className="text-brand-warm-deep mr-2">•</span>
                    Historia rezerwacji i transakcji zostanie usunięta
                  </li>
                  <li className="flex items-start">
                    <span className="text-brand-warm-deep mr-2">•</span>
                    Nie będziesz mógł odzyskać dostępu do konta po jego usunięciu
                  </li>
                  <li className="flex items-start">
                    <span className="text-brand-warm-deep mr-2">•</span>
                    Proces usunięcia może potrwać do 30 dni
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-ink-primary mb-2">
                Adres e-mail <span className="text-brand-warm-deep">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isAuthenticated}
                required
                className="w-full px-4 py-3 bg-surface-app-elevated border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="twoj@email.com"
              />
              {isAuthenticated && (
                <p className="text-xs text-ink-muted mt-2">
                  Adres e-mail powiązany z Twoim kontem
                </p>
              )}
            </div>

            {/* Reason Field */}
            <div>
              <label htmlFor="reason" className="block text-sm font-medium text-ink-primary mb-2">
                Powód usunięcia konta (opcjonalnie)
              </label>
              <textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 bg-surface-app-elevated border border-border-strong rounded-lg text-ink-primary placeholder-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent resize-none"
                placeholder="Pomóż nam się rozwijać - powiedz nam, dlaczego chcesz usunąć konto..."
              />
            </div>

            {/* Confirmation Checkbox */}
            <div className="bg-surface-app-elevated rounded-lg p-4 border border-border-strong">
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-border-strong text-brand-primary focus:ring-brand-primary focus:ring-offset-surface-card bg-surface-card cursor-pointer"
                />
                <span className="ml-3 text-sm text-ink-primary">
                  Potwierdzam, że rozumiem konsekwencje usunięcia konta i żądam trwałego usunięcia
                  mojego konta oraz wszystkich powiązanych z nim danych osobowych zgodnie z RODO.
                </span>
              </label>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-brand-warm/10 border border-brand-warm/40 rounded-lg p-4">
                <p className="text-brand-warm-deep text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button
                type="submit"
                disabled={isSubmitting || !confirmed}
                className="flex-1 px-6 py-3 bg-brand-warm hover:bg-brand-warm-deep disabled:bg-brand-warm/50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
              >
                {isSubmitting ? 'Wysyłanie...' : 'Wyślij żądanie usunięcia konta'}
              </button>
              <Link
                to={isAuthenticated ? "/" : "/login"}
                className="flex-1 px-6 py-3 bg-surface-card border border-border-strong hover:bg-surface-card-hover text-ink-primary font-medium rounded-lg transition-colors text-center"
              >
                Anuluj
              </Link>
            </div>
          </form>

          {/* Info Section */}
          <div className="mt-12 pt-8 border-t border-border-strong">
            <h3 className="text-lg font-bold text-white mb-4">Co się stanie po wysłaniu żądania?</h3>
            <div className="space-y-3 text-ink-primary text-sm">
              <div className="flex items-start">
                <span className="bg-brand-primary/50 text-white rounded w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-1 shrink-0">1</span>
                <p>Otrzymasz wiadomość e-mail z potwierdzeniem przyjęcia żądania</p>
              </div>
              <div className="flex items-start">
                <span className="bg-brand-primary/50 text-white rounded w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-1 shrink-0">2</span>
                <p>Nasz zespół zweryfikuje Twoje żądanie (może poprosić o dodatkowe potwierdzenie tożsamości)</p>
              </div>
              <div className="flex items-start">
                <span className="bg-brand-primary/50 text-white rounded w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-1 shrink-0">3</span>
                <p>Konto i dane zostaną usunięte w ciągu 30 dni od weryfikacji żądania</p>
              </div>
              <div className="flex items-start">
                <span className="bg-brand-primary/50 text-white rounded w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-1 shrink-0">4</span>
                <p>Otrzymasz końcowe potwierdzenie usunięcia konta</p>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="mt-8 bg-surface-app-elevated rounded-lg p-6 border border-border-strong">
            <h3 className="text-lg font-bold text-white mb-2">Potrzebujesz pomocy?</h3>
            <p className="text-ink-primary text-sm">
              Jeśli masz pytania dotyczące usunięcia konta lub przetwarzania danych, skontaktuj się z nami:
            </p>
            <p className="text-brand-primary text-sm mt-2">
              <a href="mailto:support@spotto.pl" className="hover:text-brand-primary transition-colors">
                support@spotto.pl
              </a>
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-surface-card border-t border-border-subtle mt-12">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="text-center">
            <p className="text-ink-muted text-sm">
              © {new Date().getFullYear()} SOLID LABS PSA. Wszystkie prawa zastrzeżone.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
